import { isCacheExpired } from 'config/cache.config';
import { put, select, takeLatest } from 'redux-saga/effects';
import { assetGroupActions } from 'reduxStore/assetGroups/slice';
import { bookingActions } from 'reduxStore/bookings/slice';
import { categoriesActions } from 'reduxStore/categories/slice';
import { accessLogsActions } from 'reduxStore/accessLogs/slice';
import { logsWindowCacheKey } from 'reduxStore/accessLogs/windowSelectors';
import { LOCKER_LOG_DAYS, LOCKER_LOG_SCOPE, lockerLogWindowRequest } from 'library/helpers/lockerHelpers';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { defaultCategoriesActions } from 'reduxStore/defaultCategories/slice';
import { devicesActions } from 'reduxStore/devices/slice';
import { floorsActions } from 'reduxStore/floors/slice';
import { maintenanceActions } from 'reduxStore/maintenance/slice';
import { peopleActions } from 'reduxStore/people/slice';
import { accessActions } from 'reduxStore/access/slice';
import { accessWindowsActions } from 'reduxStore/accessWindows/slice';
import { sitesActions } from 'reduxStore/sites/slice';
import { teamsActions } from 'reduxStore/teams/slice';
import { authActions } from 'reduxStore/auth/slice';
import { defaultSettingsActions } from 'reduxStore/defaultSettings/slice';
import { organisationActions } from 'reduxStore/organisations/slice';
import { settingsActions } from 'reduxStore/settings/slice';
import { CONSTANTS_SETTINGS } from 'config/settingsContainer';
import { selectIsBillingOnlyProfile } from 'library/helpers/billingContact';
import { appActions } from './slice';

// Dashboards that reuse the Access dashboard shell (same layout + ready-selector + non-scoped/scoped
// data), so they all hydrate via loadAccessDashboardData. Single source of truth for the three
// hydration entry points (live switch, boot, org switch) — add a shared-shell product here once and
// every hydration path picks it up. Lockers has its own shell/hydration and is intentionally absent.
export const ACCESS_SHELL_DASHBOARDS = ['access', 'bookings', 'vision'];

// A bucket exists once its load starts. Empty means the org has none, not "unfetched": refetching it
// flips the bucket to loading, which drops the dashboard-ready gate and remounts the whole page.
const needsFetch = (sliceData, orgID, siteID) => !sliceData[orgID]?.[siteID];

// This is a global listener that listens for all actions
// allows us to listen for actions and load missing resources due to errors or any other reason.
// allows us to perform side effects resulting from the multiple actions.
export function* globalSagaListener() {
	// Get the state of the application
	const state = yield select();

	// 1: prepare the user related Data and close the userLoadingComplete state
	// Check if user is logged and profile is loaded
	const { isLoggedIn, profileLoadingComplete } = state.Auth;

	// Check if the workspaces is loaded
	const { organisationLoadingComplete } = state.Organisations;

	// Check if the default settings are loaded
	const { personDefaultSettingsLoadingComplete, orgDefaultSettingsLoadingComplete } = state.DefaultSettings;

	// Check if the settings are loaded for the user and organization
	const { personSettingsLoadingComplete, orgSettingsLoadingComplete } = state.Settings;

	// Get the state of the user loading complete from the state
	const { userLoadingComplete } = state.App;

	// 🛑 BILLING-ONLY SESSION (D48): the server refuses this person every org read but billing, so none of the
	// settings below will ever arrive. The profile and the org list are all the shell needs; release the loader and
	// hydrate NOTHING. The Billing page loads what it shows.
	if (selectIsBillingOnlyProfile(state)) {
		if (!userLoadingComplete && isLoggedIn && profileLoadingComplete && organisationLoadingComplete) {
			yield put(appActions.setUserLoadingComplete());
		}
		return;
	}

	// Check if the user is logged in and the user loading is not complete
	if (!userLoadingComplete) {
		// check if all the required data is loaded
		if (
			profileLoadingComplete &&
			isLoggedIn &&
			organisationLoadingComplete &&
			personSettingsLoadingComplete &&
			orgSettingsLoadingComplete &&
			personDefaultSettingsLoadingComplete &&
			orgDefaultSettingsLoadingComplete
		) {
			// If all the data is loaded, then set the user resource loading complete
			yield put(appActions.setUserLoadingComplete());

			//* Once the user's profile is loaded, then we can send user to the dashboard
			//* ?This is understood that this is a temp for now as we work with the settings to load the correct dashboard user is part of,
			//* ?therefore we are sending user to the access dashboard until we implement the functionality to look up the settings and deploy restricted loading etc.
			//* ?The restricted loading will include preventing user from switching the dashboard if the workspace is not configured for the respective dashboard.
			// ? This logic has moved to the Dashboard Chooser.
			// ? All the logic to decide which dashboard to redirect the user
			// ? to need to be implemented in the Dashboard Chooser
			// yield put(appActions.switchDashboard('access'));

			//Based on the current dashboard selection, we can load the dashboard data
			const selectedDashboard = yield select((state) => state.App.dashboard.selected);

			// Access-shell products (Access/Bookings/Vision) all hydrate the same way.
			if (ACCESS_SHELL_DASHBOARDS.includes(selectedDashboard)) {
				yield put(appActions.loadAccessDashboardData());
			}
			if (selectedDashboard === 'lockers') {
				yield put(appActions.loadLockersDashboardData());
			}
		}
	}
}
// Switch listener when the user changes the dashboard
export function* switchDashboard(action) {
	if (selectIsBillingOnlyProfile(yield select())) return;
	// Access-shell products (Access/Bookings/Vision) share the same shell/hydration.
	if (ACCESS_SHELL_DASHBOARDS.includes(action.payload)) {
		yield put(appActions.loadAccessDashboardData());
	}
	// Check if the user has selected the lockers dashboard
	if (action.payload === 'lockers') {
		// Lockers is org-wide: pin scope to 'all' BEFORE hydrating. A site scope
		// carried over from Access starves lockers selectors keyed on
		// selectedProfile.siteID; pinning first lets the scope coordinator do the
		// (single) refetch and loadLockersDashboardData's bucket guards skip the
		// in-flight loads. This is the only entry path that can carry a narrowed
		// scope — boot starts at 'all' and org-switch resets to 'all'.
		const selectedSiteID = yield select((state) => state.App.selectedSiteID);
		if (selectedSiteID !== 'all') {
			yield put(appActions.setSelectedSite('all'));
		}
		yield put(appActions.loadLockersDashboardData());
	}
}

// Load the access dashboard data — NON-SCOPED entities only.
//
// Scoped entities (people, teams, devices, credentials, permissions, access,
// accessLogs, assetGroups) are loaded exclusively by `onScopeChange`, which
// is triggered by `appActions.setSelectedSite`. The hydration hook
// (`useScopeUrlSync`) always dispatches `setSelectedSite` once sites are
// available, so scoped data is guaranteed to load without duplicating here.
export function* loadAccessDashboardData() {
	const state = yield select();
	// Billing-only (D48): every one of these reads is refused to this person.
	if (selectIsBillingOnlyProfile(state)) return;

	const { orgID } = state.Auth.selectedProfile;

	// ── Non-scoped entities (global cache checks) ───────────────────

	// Sites State
	const { data: sitesData, sitesLoadingComplete, loading: sitesLoading, timeStamp: sitesTimeStamp } = state.Sites;

	// Floors State
	const {
		data: floorsData,
		floorsLoadingComplete,
		loading: floorsLoading,
		timeStamp: floorsTimeStamp
	} = state.Floors;

	// Default Categories
	const { loading: loadingDefaultCategories, loadingComplete: defaultCategoriesLoadingComplete } =
		state.DefaultCategories;

	// Categories State
	const {
		data: categoriesData,
		categoriesLoadingComplete,
		loading: loadingCategories,
		timeStamp: categoriesTimeStamp
	} = state.Categories;

	// If Sites cache data is expired or not loaded, then load the sites data
	if (isCacheExpired(sitesTimeStamp, 1) || (!sitesLoadingComplete && !sitesLoading) || !(orgID in sitesData)) {
		yield put(sitesActions.loadSites());
	}

	// If Floors cache data is expired or not loaded, then load the floors data
	if (isCacheExpired(floorsTimeStamp, 1) || (!floorsLoadingComplete && !floorsLoading) || !(orgID in floorsData)) {
		yield put(floorsActions.loadFloors());
	}

	// If Default Categories cache data is expired or not loaded, then load the Default Categories Data
	if (!defaultCategoriesLoadingComplete && !loadingDefaultCategories) {
		yield put(defaultCategoriesActions.loadDefaultCategories());
	}

	//If Categories data is expired or not loaded, then load the categories data
	if (
		isCacheExpired(categoriesTimeStamp, 1) ||
		(!categoriesLoadingComplete && !loadingCategories) ||
		!(orgID in categoriesData)
	) {
		yield put(categoriesActions.loadCategories());
	}

	// AccessWindows (org-level) are loaded by onScopeChange, which always runs on
	// boot (useScopeUrlSync dispatches setSelectedSite once sites arrive) and on
	// every org switch (switchProfileSuccess resets the scope key). Loading them
	// here too fired a duplicate /accessWindows request on every login.
}

// Load the lockers dashboard data
// People and Devices are shared — Lockers always reads from 'all' bucket
export function* loadLockersDashboardData() {
	const state = yield select();
	if (selectIsBillingOnlyProfile(state)) return;

	//✅ Get the global states to check if the data is loaded
	const { orgID } = state.Auth.selectedProfile;

	// ── Non-scoped entities (global cache checks) ───────────────────

	// Sites State
	const { data: sitesData, sitesLoadingComplete, loading: sitesLoading, timeStamp: sitesTimeStamp } = state.Sites;

	// Floors State
	const {
		data: floorsData,
		floorsLoadingComplete,
		loading: floorsLoading,
		timeStamp: floorsTimeStamp
	} = state.Floors;

	// Categories State
	const {
		data: categoriesData,
		categoriesLoadingComplete,
		loading: loadingCategories,
		timeStamp: categoriesTimeStamp
	} = state.Categories;

	// Bookings State
	const {
		data: bookingsData,
		bookingsLoadingComplete,
		loading: loadingBooking,
		timeStamp: bookingTimeStamp
	} = state.Bookings;

	// Lockers logs live in the shared access-log window cache (accessLogs slice).
	const lockersLogWindow =
		state.AccessLogs.logsWindow?.[orgID]?.[logsWindowCacheKey(LOCKER_LOG_SCOPE, LOCKER_LOG_DAYS)];

	// Maintenance Logs State
	const {
		data: maintenanceLogs,
		maintenanceLoadingComplete,
		loading: maintenanceLogsLoading,
		timeStamp: maintenanceLogsTimeStamp
	} = state.Maintenance;

	// If Sites cache data is expired or not loaded, then load the sites data
	if (isCacheExpired(sitesTimeStamp, 1) || (!sitesLoadingComplete && !sitesLoading) || !(orgID in sitesData)) {
		yield put(sitesActions.loadSites());
	}

	// If Floors cache data is expired or not loaded, then load the floors data
	if (isCacheExpired(floorsTimeStamp, 1) || (!floorsLoadingComplete && !floorsLoading) || !(orgID in floorsData)) {
		yield put(floorsActions.loadFloors());
	}

	//If Categories data is expired or not loaded, then load the categories data
	if (
		isCacheExpired(categoriesTimeStamp, 1) ||
		(!categoriesLoadingComplete && !loadingCategories) ||
		!(orgID in categoriesData)
	) {
		yield put(categoriesActions.loadCategories());
	}

	// ── Shared scoped entities — Lockers always uses 'all' bucket ────
	// Pass { siteID: 'all' } to override the profile's siteID

	// If Devices 'all' bucket is empty or missing, fetch
	if (needsFetch(state.Devices.data, orgID, 'all')) {
		yield put(devicesActions.loadDevices({ siteID: 'all' }));
	}

	// If People 'all' bucket is empty or missing, fetch
	if (needsFetch(state.People.data, orgID, 'all')) {
		yield put(peopleActions.loadPerson({ siteID: 'all' }));
	}

	// ── Non-scoped Lockers entities ─────────────────────────────────

	// If Bookings data is expired or not loaded, then load the bookings data
	if (
		isCacheExpired(bookingTimeStamp, 1) ||
		(!bookingsLoadingComplete && !loadingBooking) ||
		!(orgID in bookingsData)
	) {
		yield put(bookingActions.loadBookings());
	}

	// If the lockers log window is expired or absent, load it. The saga's own TTL
	// makes a redundant dispatch a no-op, so this only has to be not-wrong.
	if (!lockersLogWindow || isCacheExpired(lockersLogWindow.ts, 1)) {
		yield put(accessLogsActions.loadLogsWindow(lockerLogWindowRequest()));
	}

	// If the maintenance logs data is expired or not loaded, then load the logs data
	if (
		isCacheExpired(maintenanceLogsTimeStamp, 1) ||
		(!maintenanceLoadingComplete && !maintenanceLogsLoading) ||
		!(orgID in maintenanceLogs)
	) {
		yield put(maintenanceActions.loadMaintenanceLog());
	}
}

// What the device/gateway drawers read, loaded when one opens — on the Lockers boot this
// was 7 requests nobody read. Guarded, so the Access shell re-asks for nothing.
export function* ensureDrawerEntities() {
	const state = yield select();
	if (selectIsBillingOnlyProfile(state)) return;
	const { orgID } = state.Auth.selectedProfile;
	// These sagas read the live scope and would fill the wrong bucket under a site scope.
	if (!orgID || state.App.selectedSiteID !== 'all') return;

	if (needsFetch(state.Teams.data, orgID, 'all')) {
		yield put(teamsActions.loadTeams());
	}
	if (needsFetch(state.AssetGroups.data, orgID, 'all')) {
		yield put(assetGroupActions.loadGroups());
	}
	// Access bucket has no `.data` array (shape: {people, teams, ...}) — needsFetch doesn't apply
	if (!state.Access.data?.[orgID]?.['all']) {
		yield put(accessActions.getAccessPermissions());
	}
}

// Scope Change Coordinator — re-fetches all scoped entities when the user switches site.
// Each saga reads { orgID, siteID } from state.Auth.selectedProfile.
// takeLatest cancels the previous coordinator if user switches fast.
//
// Dedupe: boot hydration dispatches setSelectedSite several times with the
// SAME scope — without this guard each dispatch re-fired all 8 loads
// (measured at service-account login: access ×4, devices/people/teams/
// credentials/deviceGroups/access-windows ×2 ≈ 1 MB duplicate transfer).
// takeLatest can't help: the coordinator finishes instantly and the HTTP
// requests have already left. A real site/org switch changes the key and
// still refreshes everything. The key lives in the App slice (not module
// scope) so logout resets it with the rest of the state, devtools can see
// it, and tests don't leak it between cases.
export function* onScopeChange() {
	// Billing-only (D48): no scoped entity is readable, so a scope change loads nothing (and records no key).
	if (yield select(selectIsBillingOnlyProfile)) return;
	const orgID = yield select((state) => state.Auth.selectedProfile?.orgID);
	const selectedSiteID = yield select((state) => state.App.selectedSiteID);
	const scopeKey = `${orgID}:${selectedSiteID}`;
	const lastKey = yield select((state) => state.App.hydratedScopeKey);
	if (scopeKey === lastKey) return;
	yield put(appActions.setHydratedScopeKey(scopeKey));

	yield put(peopleActions.loadPerson());
	yield put(credentialsActions.loadCredentials());
	yield put(devicesActions.loadDevices());
	yield put(teamsActions.loadTeams());
	yield put(assetGroupActions.loadGroups());
	yield put(accessActions.getAccessPermissions());
	yield put(accessLogsActions.loadAccessLogs());
	yield put(accessLogsActions.loadTodayCounts());
	yield put(accessWindowsActions.loadAccessWindows());
	yield put(credentialsActions.fetchPropagation());

	/*
	 * 🛑 NOTHING IS DISPATCHED FOR CAMERAS, and its absence is load-bearing.
	 *
	 * A camera is a device row: `loadDevices` above already returns them, and
	 * `getAllDevicesAllLocations` already places them, so the Cameras page derives its list
	 * from the devices bucket (see reduxStore/cameras/selectors). A camera read here was
	 * three requests re-asking for data this coordinator had just fetched.
	 *
	 * ⚠️ It was also a RACE, and that is the part not to reintroduce. The read was gated on
	 * `selectIsCamerasModuleEnabled`, which reads org settings — and `useScopeUrlSync`
	 * dispatches `setSelectedSite` as soon as SITES land, which can be before settings do.
	 * The flag read false, the read never fired, and `hydratedScopeKey` was already written
	 * so this coordinator could never run again for the scope. The Cameras page then fetched
	 * for itself on mount: a loading screen on a dashboard that had finished loading.
	 * Deriving from devices removes the gate, the request and the race together.
	 */

	// Only fetch external time periods if Inner Range module is active
	const orgSettings = yield select((state) => {
		const orgID = state.Auth.selectedProfile?.orgID;
		return state.Settings.mergedOrgSettings?.[orgID] || [];
	});
	const isIRActive = orgSettings.some(
		(s) => s.defaultSettingID === CONSTANTS_SETTINGS.INNER_RANGE_MODULE && Number(s.value) === 1
	);
	if (isIRActive) {
		yield put(accessWindowsActions.loadExternalTimePeriods());
	}
}

// The 7 actions that are the ONLY ones that can flip the 8 boot-ready flags checked
// by globalSagaListener. Replacing takeLeading('*') cuts the per-action overhead from
// ~50+ firings on boot down to exactly these 7 meaningful signals.
const BOOT_READY_ACTIONS = [
	authActions.getUserProfileSuccess,
	authActions.loginSuccess,
	organisationActions.loadOrganisationsSuccess,
	defaultSettingsActions.loadOrgDefaultSettingsSuccess,
	defaultSettingsActions.loadPersonDefaultSettingsSuccess,
	settingsActions.loadOrgSettingsSuccess,
	settingsActions.loadPersonSettingsSuccess
];

// The main export for the saga
export default function* appSagas() {
	yield takeLatest(BOOT_READY_ACTIONS, globalSagaListener);
	yield takeLatest(appActions.switchDashboard, switchDashboard);
	yield takeLatest(appActions.loadAccessDashboardData, loadAccessDashboardData);
	yield takeLatest(appActions.loadLockersDashboardData, loadLockersDashboardData);
	yield takeLatest(appActions.setSelectedSite, onScopeChange);
	yield takeLatest(appActions.openDrawer, ensureDrawerEntities);
}
